import { defineStore } from 'pinia';
import { ref, shallowRef, computed, nextTick, markRaw, type Component } from 'vue';
import { useSlideoutStore as useMainSlideoutStore } from '@nuxt-common/atomic-ui/app/stores/useSlideoutStore';
import { isRaw } from '~/utils/isRaw';

/**
 * Defines the basic structure for a slideout component's configuration,
 * including its identifier, content, and display properties.
 */
export type BasicSlideoutType = {
  id: string;
  title?: string;
  class?: string;
  component: Component;
  footerComponent?: Component;
  props?: Record<string, unknown>;
  rootProps?: Record<string, unknown>;
  providerProps?: Record<string, unknown>;
  footerProps?: Record<string, unknown>;
  backable?: boolean;
  position?: string;
  evtHandlers?: Record<string, () => void>;
  footerEvtHandlers?: Record<string, () => void>;
  onClose?: () => void;
  onBack?: () => void;
};

/**
 * Represents a node in the slideout history.
 * It contains the slideout configuration and pointers (`uuid`, `prevUUID`)
 * to form a linked list for navigation.
 */
export type SlideoutNodeType = {
  uuid: string;
  prevUUID: string;
  slideout: BasicSlideoutType;
};

/**
 * @description A Pinia store to manage the state and lifecycle of local slideouts within the PDP context.
 * This store employs a singly linked list data structure, implicitly formed by `SlideoutNodeType` objects
 * each referencing a `prevUUID`, to track the history of slideouts for navigation purposes.
 *
 * This store handles the currently active slideout, a history of slideouts for navigation,
 * and actions to show, add, or go back. It integrates with a main slideout store
 * from `@nuxt-common/atomic-ui` to control the expanded/collapsed visual state, but manages
 * the content and history independently.
 */
export const useSlideoutStore = defineStore('slideoutLocal', () => {
  // --- STORE INSTANCES ---

  /**
   * @internal
   * An instance of the main UI slideout store to control the panel's visibility.
   */
  const slideoutStore = useMainSlideoutStore();

  // --- STATE ---

  /**
   * @internal
   * Holds the unique identifier (UUID) of the currently active slideout.
   * An empty string signifies that no slideout is active.
   * @type {import('vue').Ref<string>}
   */
  const currentUUID = ref<string>('');
  /**
   * @internal
   * Holds a collection of all slideouts in the current navigation stack.
   * This is a Map for efficient access, but logically it functions as a singly linked list
   * due to each `SlideoutNodeType` storing a `prevUUID` reference to its predecessor.
   * @type {import('vue').ShallowRef<Map<string, SlideoutNodeType>>}
   */
  const slideoutHistory = shallowRef<Map<string, SlideoutNodeType>>(new Map());

  // --- GETTERS ---

  /**
   * Holds the currently active slideout configuration.
   * Is `null` if no slideout is currently displayed.
   * @type {import('vue').ComputedRef<BasicSlideoutType | null>}
   */
  const currentSlideout = computed<BasicSlideoutType | null>(() => slideoutHistory.value.get(currentUUID.value)?.slideout ?? null);

  /**
   * Checks if a slideout is currently being displayed.
   * @returns {boolean} `true` if a slideout is active, `false` otherwise.
   */
  const isAvailable = computed<boolean>(() => Boolean(currentSlideout.value));

  // --- ACTIONS ---

  /**
   * @internal
   * Internal helper to mark a slideout's component with `markRaw`.
   * This prevents deep reactivity on the component definition itself, improving performance.
   * @param {Component} component The slideout component to mark as raw.
   */
  const markSlideoutCompRaw = (component: Component) => {
    if (!isRaw(component)) {
      markRaw(component);
    }
  };

  /**
   * @internal
   * Creates a new slideout history node.
   * It links the new slideout to the previous one by storing the current UUID.
   * @param {string} randomUUID The unique identifier for the new slideout instance.
   * @param {BasicSlideoutType} slideout The slideout object to create a node for.
   * @returns {SlideoutNodeType} The new slideout node.
   */
  const getSlideoutNode = (randomUUID: string, slideout: BasicSlideoutType): SlideoutNodeType => ({
    uuid: randomUUID,
    prevUUID: currentUUID.value,
    slideout,
  });

  /**
   * Adds a new slideout to the navigation history and immediately sets it as the active slideout.
   * This involves marking the component as raw to optimize performance, creating a history node,
   * storing it in the `slideoutHistory` map, and updating `currentUUID` to reflect the new active slideout.
   * @param {string} randomUUID The unique identifier for this slideout instance.
   * @param {BasicSlideoutType} slideout The slideout object to add to the history.
   */
  function addSlideout(randomUUID: string, slideout: BasicSlideoutType) {
    // makes sure component obj is mutated with markRaw operation, to prevent unintended perf overhead by vue
    markSlideoutCompRaw(slideout.component);
    if (slideout?.footerComponent) {
      markSlideoutCompRaw(slideout.footerComponent);
    }

    const slideoutNode = getSlideoutNode(randomUUID, slideout);
    slideoutHistory.value.set(randomUUID, slideoutNode);
    currentUUID.value = randomUUID;
  }

  /**
   * Adds a slideout and ensures its corresponding UI panel is toggled open.
   * It uses `nextTick` to wait for the DOM to update with the new slideout component
   * before triggering the toggle action on the main UI store.
   * @param {string} randomUUID The unique identifier for this slideout instance.
   * @param {BasicSlideoutType} slideout The slideout object to show.
   */
  function showSlideout(randomUUID: string, slideout: BasicSlideoutType) {
    addSlideout(randomUUID, slideout);
    nextTick(() => {
      // Wait for the slideout component to be rendered before toggling its visibility.
      slideoutStore.toggle(slideout.id);
    });
  }

  /**
   * Executes the "back" action for the slideout navigation.
   * If there are multiple slideouts in the history, it sets the previous slideout as the current one
   * and removes the now-deactivated slideout from the history.
   * If only one slideout exists, this function does nothing, as there is no previous slideout to go back to.
   * Visual updates (closing the current slideout and opening the previous one) are handled reactively
   * by the consuming components based on changes to `currentSlideout`.
   */
  function backSlideout() {
    if (slideoutHistory.value.size > 1) {
      // Get the UUID of the slideout to navigate back to.
      const currentKey = currentUUID.value;
      const prevKey = slideoutHistory.value.get(currentKey)?.prevUUID ?? '';
      currentUUID.value = prevKey;

      // Remove the now-closed slideout from the history map.
      slideoutHistory.value.delete(currentKey);
    }
  }

  function closeSlideout() {
    const currentId = currentSlideout.value?.id ?? '';
    if (currentId && slideoutStore.expanded[currentId]) {
      slideoutStore.toggle(currentId);
    }
  }

  /**
   * Clears the entire slideout history and effectively closes the current slideout by resetting the state.
   */
  function clearSlideoutHistory() {
    currentUUID.value = '';
    slideoutHistory.value.clear();
  }

  return {
    // getters
    isAvailable,
    currentSlideout,

    // actions
    addSlideout,
    showSlideout,
    backSlideout,
    closeSlideout,
    clearSlideoutHistory,
  };
});
